/* eslint-disable max-classes-per-file */
import type { HttpErrorResponse } from '@angular/common/http';
import type { Params } from '@angular/router';
import type { Action } from '@ngrx/store';
import type { MinimalActivatedRouteSnapshot } from '@ngrx/router-store';

import type { JssState } from '../jss-state.model';

export const enum JssStateActionTypes {
  UPDATE = '[JssState] update state',
  ERROR = '[JssState] error state',
  CHANGE_ROUTE = '[JssState] change route state',
  ACTIVATE_ERROR_ROUTE = '[JssState] activate error route',
  EXPERIENCE_EDITOR_ACTIVE = '[JssState] experience editor active',
  EXPERIENCE_EDITOR_SERVER_ACTIVE = '[JssState] experience editor server active',
  FATAL_ERROR = '[JssState] fatal error',
  META_TITLE_UPDATED = '[JssState] meta title updated',
  META_LINK_UPDATED = '[JssState] meta link updated',
  ORGANIZATIONAL_DATA_UPDATED = '[JssState] organizational data updated',
  UPDATE_HISTORY = '[JssState] Update history',
  ROOT_PLACEHOLDER_LOADED = '[JssState] Root placeholder loaded',
  DYNAMIC_PLACEHOLDER_LOADED = '[JssState] Dynamic placeholder loaded',
}

export interface JssUrl {
  route: string;
  language: string;
  extraQueryParams?: Params;
}

export interface JssUrlWithStatus extends JssUrl {
  statusCode: number;
}

export interface JssFatalError {
  route: string;
  error: Error;
}

export interface MetaLink {
  type: 'canonical' | 'amphtml';
  url: string;
}

export class JssStateUpdate implements Action {
  readonly type = JssStateActionTypes.UPDATE;
  public constructor(public payload: JssState) {}
}

export class JssStateError implements Action {
  readonly type = JssStateActionTypes.ERROR;
  public constructor(public payload: HttpErrorResponse) {}
}

export class JssStateFatalError implements Action {
  readonly type = JssStateActionTypes.FATAL_ERROR;
  public constructor(public payload: JssFatalError) {}
}

export class JssStateChangeRoute implements Action {
  readonly type = JssStateActionTypes.CHANGE_ROUTE;
  public constructor(public payload: JssUrl) {}
}

export class JssStateActiveErrorRoute implements Action {
  readonly type = JssStateActionTypes.ACTIVATE_ERROR_ROUTE;
  public constructor(public payload: JssUrlWithStatus) {}
}

export class JssStateExperienceEditorActive implements Action {
  readonly type = JssStateActionTypes.EXPERIENCE_EDITOR_ACTIVE;
  public constructor(public payload: boolean) {}
}

export class JssStateExperienceEditorServerActive implements Action {
  readonly type = JssStateActionTypes.EXPERIENCE_EDITOR_SERVER_ACTIVE;
  public constructor(public payload: boolean) {}
}

export class JssStateMetaTitleUpdated implements Action {
  readonly type = JssStateActionTypes.META_TITLE_UPDATED;
  public constructor(public payload: string) {}
}

export class JssStateMetaLinkUpdated implements Action {
  readonly type = JssStateActionTypes.META_LINK_UPDATED;
  public constructor(public payload: MetaLink) {}
}

export class JssStateOrganizationalDataUpdated implements Action {
  readonly type = JssStateActionTypes.ORGANIZATIONAL_DATA_UPDATED;
  public constructor(public payload?: string) {}
}

/**
 * NOTE: Keep in mind that some pages have multiple root placeholders
 */
export class JssStateRootPlaceholderLoaded implements Action {
  readonly type = JssStateActionTypes.ROOT_PLACEHOLDER_LOADED;
  public constructor(public payload?: string) {}
}

/**
 * Normally scrolling to anchors will start as soon as the root (static) placeholders are loaded.
 * When you have a very important dynamic placeholder you want to wait for before starting to scroll,
 * you can call this action.
 *
 * NOTE(1): This is currently only used in the dynamic-row column placeholders since
 *          the content in these columns may contain anchors that should be loaded before scrolling to them.
 *          But this should also work for any other dynamic placeholder.
 *
 * NOTE(2): Using this may have a bad impact on UX, it will wait longer before actually starting to scroll down to a #fragment
 */
export class JssStateDynamicPlaceholderLoaded implements Action {
  readonly type = JssStateActionTypes.DYNAMIC_PLACEHOLDER_LOADED;
  public constructor(public payload?: string) {}
}

export class JssStateUpdateHistory implements Action {
  readonly type = JssStateActionTypes.UPDATE_HISTORY;
  public constructor(
    public payload: MinimalActivatedRouteSnapshot['queryParams']
  ) {}
}

export type JssStateActionsUnion =
  | JssStateUpdate
  | JssStateError
  | JssStateChangeRoute
  | JssStateActiveErrorRoute
  | JssStateExperienceEditorActive
  | JssStateExperienceEditorServerActive
  | JssStateMetaTitleUpdated
  | JssStateMetaLinkUpdated
  | JssStateOrganizationalDataUpdated
  | JssStateRootPlaceholderLoaded
  | JssStateUpdateHistory
  | JssStateDynamicPlaceholderLoaded;
